import React, { useState } from 'react';
import styled from 'styled-components';

import MaterialSymbol from '#/components/basic/icons/MaterialSymbol';
import ToggleSlider from '#/components/basic/inputs/ToggleSlider/ToggleSlider';
import ModalContent from '#/components/basic/modal/components/ModalContent';
import ModalFooterButtons from '#/components/basic/modal/components/ModalFooterButtons';
import SimpleModalOrMobilePage from '#/components/basic/modal/SimpleModalOrMobilePage';
import MarginTypeChip from '#/components/markets/intermittent/MarginTypeChip';
import { closedIsolatedMarginWarningModal } from '#/reduxState/exchange/userGuideReducer';
import { staticDispatch } from '#/reduxState/store';

const CenterWrap = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  text-align: center;
`;

const IconCircle = styled.div`
  width: 64px;
  height: 64px;
  color: var(--yellow0);
  font-size: 64px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const Title = styled.div`
  text-align: center;
  font-size: var(--medium2);
  color: var(--white);
`;

const Description = styled.p`
  color: var(--gray4c);
  font-size: var(--small);
  line-height: 1.5;
  margin: 4px 0 0;
  padding: 0 36px;
`;

const ToggleLabel = styled.span`
  font-size: var(--small);
`;

type Props = {
  market: string;
  onClose: () => void;
  onContinue: () => void;
};

/**
 * Warning shown before the first order placement in an isolated margin market
 * (skipped when the wallet already has an open order or position in the market).
 */
export default function IsolatedMarginWarningModal({
  market,
  onClose,
  onContinue,
}: Props) {
  const [dontShowAnymore, setDontShowAnymore] = useState(false);
  const persistDontShow = () => {
    if (dontShowAnymore) {
      staticDispatch(closedIsolatedMarginWarningModal());
    }
  };
  return (
    <SimpleModalOrMobilePage
      title="Margin Confirmation"
      onClose={() => {
        persistDontShow();
        onClose();
      }}
      size="small"
      padding="0"
      verticalAlignOnMobile="ceneteredDiv"
    >
      <ModalContent>
        <CenterWrap>
          <IconCircle>
            <MaterialSymbol icon="info" fontSize="inherit" filled />
          </IconCircle>
          <Title>
            {market} uses <MarginTypeChip marginType="isolated" /> margin
          </Title>
          <Description>
            While you have open orders or positions in this market, you cannot
            place orders in any other market.
            <br />
            Close them to trade elsewhere.
          </Description>
        </CenterWrap>
      </ModalContent>
      <ModalFooterButtons
        confirmButtonProps={{
          inline: true,
          onClick: () => {
            persistDontShow();
            onContinue();
          },
          children: 'Continue',
        }}
      >
        <ToggleSlider
          checked={dontShowAnymore}
          onChange={setDontShowAnymore}
          inline
        >
          <ToggleLabel>Don't show this message again</ToggleLabel>
        </ToggleSlider>
      </ModalFooterButtons>
    </SimpleModalOrMobilePage>
  );
}
